import { Box, Stack } from "@mui/material";
import { useSyncAccountsWhenRegionReady } from "Hooks/useSyncAccountsWhenRegionReady";
import { BalanceWithPaymentActions } from "Components/Dashboard/BalanceWithPaymentActions/BalanceWithPaymentActions";
import { ConnectedAccounts } from "Components/Dashboard/ConnectedAccounts/ConnectedAccounts";
import { DocumentExpirationWarningPopup } from "Components/Dashboard/DocumentExpirationWarningPopup";
import { MooneLitePopup } from "Components/Dashboard/MooneLitePopup";
import { SetupChecklistCard } from "Components/Dashboard/SetupChecklist/SetupChecklistCard";
import { TransactionsComponent } from "Components/Dashboard/TransactionsComponent";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { HeroSection } from "Components/Layout/HeroBackground/HeroSection";
import { MobileAppInstallBanner } from "Components/MobileApp/MobileAppInstallBanner/MobileAppInstallBanner";
import { BlDivider } from "Components/Shared/BlDivider";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { AccountSelectionDialog } from "Components/Banking/AccountSelectionDialog/AccountSelectionDialog";
import { useIsMobile } from "Hooks/useIsMobile";
import { useBankConnectionSuccessHandler } from "Hooks/useBankConnectionSuccessHandler";
import { resetTransactionListPagination } from "State/Transactions/TransactionReducer";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Resources, useResource } from "Translations/Resources";
import { CombinedEarningsSummary } from "Components/Affiliate/Earnings/CombinedEarningsSummary";
import { PendingRoleIntentsBanner } from "Components/Roles/PendingRoleIntentsBanner";
import { RaffleDashboardCard } from "Components/Raffle/RaffleDashboardCard";
import { HandleMissingBanner } from "Components/Social/HandleMissingBanner";
import { StoriesSection } from "Components/Stories/StoriesSection";

const EARNINGS_RESOURCES = Resources.Components.Affiliate.Earnings;

export const CustomerDashboardPage = () => {
  useSyncAccountsWhenRegionReady();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const { t } = useResource();
  const {
    shouldShowDialog,
    isDialogOpen,
    bankAccounts,
    bankName,
    isLoading,
    onClose,
    onConfirm,
    isPending,
  } = useBankConnectionSuccessHandler();

  useEffect(() => {
    return () => {
      dispatch(resetTransactionListPagination());
    };
  }, [dispatch]);

  return (
    <AuthenticatedLayout
      title={t(Resources.Pages.Titles.Dashboard)}
      isTitleHiddenBelowLgScreen
      isPaddingDisabled={isMobile}
    >
      <StyledPageWrapper>
        <Box className="left-content">
          <Stack mb={6} mx={isMobile ? 1 : 0} gap={isMobile ? 2 : 1}>
            <HeroSection gap={isMobile ? 2 : 1}>
              <PendingRoleIntentsBanner />
              <BalanceWithPaymentActions />
              {/* The stories strip bleeds to the screen edge on mobile: cancel this Stack's own
                  right margin so the last visible circle is cut by the screen, not before it. */}
              <Box mr={isMobile ? -1 : 0}>
                <StoriesSection />
              </Box>
            </HeroSection>
            <MobileAppInstallBanner />
            <SetupChecklistCard />
            <HandleMissingBanner />
            <DocumentExpirationWarningPopup />
            <Stack mt={isMobile ? 2 : 1}>
              <CombinedEarningsSummary
                payoutHint={t(EARNINGS_RESOURCES.CombinedPayoutHint)}
                totalEarnedHint={t(EARNINGS_RESOURCES.CombinedTotalEarnedHint)}
              />
            </Stack>
            <RaffleDashboardCard />
            <ConnectedAccounts />
            <MooneLitePopup isLiteVariantHidden />
            <BlDivider />
            <Box mx={2}>
              <TransactionsComponent />
            </Box>
          </Stack>
        </Box>
      </StyledPageWrapper>
      {shouldShowDialog && bankAccounts && (
        <AccountSelectionDialog
          open={isDialogOpen}
          onClose={onClose}
          onConfirm={onConfirm}
          accounts={bankAccounts}
          bankName={bankName}
          isLoading={isLoading}
          isPending={isPending}
        />
      )}
    </AuthenticatedLayout>
  );
};

export default CustomerDashboardPage;
